import { CachedProjectService } from "../../../../shared/services/cached-project.service";
import { Subject } from "rxjs";
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { JobDetail } from "../../../../models/jobDetail";
import { take, takeUntil } from "rxjs/operators";
import * as d3 from "d3";


@Component({
    selector: "app-source-plotting",
    templateUrl: "./source-plotting.component.html",
    styleUrls: ["./source-plotting.component.less"],
    standalone: false
})
export class SourcePlottingComponent {
  @ViewChild("graph", { static: true }) graph: ElementRef;
  @ViewChild("graphContainer", { static: true }) container: ElementRef;

  @Input() shotsData!: any[];
  @Input() activeShotId!: any;
  @Input() shot!: any;
  @Input() iteration: number;
  @Input() height: number;
  @Input() startTime: number;
@Input() endTime: number;
  private destroy$ = new Subject<void>();
  job: JobDetail;
  isLoading: boolean = false;
  totalTime: any;
  data: any[] = [];
  timeLimit: any = 1;
  frequencyLimit: any = 50;
  maxFrequency: any;

  // d3 graph variables
  svg: any;
  xScale: any;
  yScale: any;
  xAxis: any;
  yAxis: any;
  zoom: any;
  line: any;
  path: any;
  errorMessage: any;

  // spectrum plot variables
  spectrumSvg: any;
  spectrumXScale: any;
  spectrumYScale: any;
  spectrumXAxis: any;
  spectrumYAxis: any;
  spectrumLine: any;
  spectrumPath: any;
  frequencies: number[] = [];
  amplitudes: number[] = [];


  constructor(private cachedProjectService: CachedProjectService) {
    this.cachedProjectService.currentJob
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (job: JobDetail) => {
          this.job = job;
          this.updateSrcPlot();
        },
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if ("activeShotId" in changes && this.shotsData) {
      this.updateSrcPlot();
    }
    if (changes["height"]) {
      this.updateHeight();
    }
    if (changes["startTime"] || changes["endTime"]) {
      this.applySynchronizedTimeRange();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {}

timeDomainWidthRatio: number = 0.33; 
spectrumWidthRatio: number = 0.66;   


private getWidthRatios(): { timeDomain: number, spectrum: number } {
  const screenWidth = window.innerWidth;
  if (screenWidth < 1000) {
    return { timeDomain: 0.9, spectrum: 0.9 };
  }
  return { timeDomain: this.timeDomainWidthRatio, spectrum: this.spectrumWidthRatio };
}

private applySynchronizedTimeRange() {
  
  if (this.startTime != null && this.endTime != null && this.data && this.data.length > 0) {
    
    this.timeLimit = this.endTime;
    
    
    this.rescale(this.endTime, this.startTime);
  }
}


@HostListener('window:resize', ['$event'])
onResize(event: any) {
  if (this.data && this.data.length > 0) {
    // Redraw both plots with new dimensions
    this.makeSrcPlot({ time: this.totalTime, data: this.data });
  }
}

  updateSrcPlot() {
    if (!this.activeShotId || !this.iteration) return;

    let params_string = `csref=${this.activeShotId}&iteration=${this.iteration}`;

    // Remove previous svg if exists
    d3.select(this.graph?.nativeElement)?.selectAll("*").remove();
    this.errorMessage = null;

    this.isLoading = true;
    this.cachedProjectService
      .getTraceSourceData(this.job.id, params_string, true)
      .pipe(take(1))
      .subscribe({
        next: (src_data) => {
          
          this.totalTime = src_data["time"];
          this.data = src_data["data"];
          this.amplitudes = src_data["amplitudes"];
          this.frequencies = src_data["frequencies"];
          this.maxFrequency = this.frequencies && this.frequencies.length > 0 ? this.frequencies[this.frequencies.length - 1] : 0;
          this.makeSrcPlot(src_data);
          this.isLoading = false;
        },
        error: (err) => {
          this.errorMessage =
            err?.error.error || "An unexpected error occurred";
          this.isLoading = false;
        },
      });
  }

  makeSrcPlot(src_data: { time: number; data: any[] }) {
    const fullContainerWidth = this.container.nativeElement.offsetWidth;
    const ratios = this.getWidthRatios();
    const containerWidth = fullContainerWidth * ratios.timeDomain;
    const srcMargin = { top: 40, right: 20, bottom: 20, left: 20 }; 
    const { time, data } = src_data;

    const width = containerWidth - srcMargin.left - srcMargin.right;
    const height = this.height;

    // Remove previous svg if exists
    d3.select(this.graph.nativeElement).selectAll("*").remove();
    // Also remove any spectrum plots
    d3.select(this.graph.nativeElement.parentElement).selectAll(".spectrum-plot").remove();

    // Create SVG
    this.svg = d3
      .select(this.graph.nativeElement)
      .attr("width", width + srcMargin.left + srcMargin.right)
      .attr("height", height + srcMargin.top + srcMargin.bottom)
      .append("g")
      .attr("class", "graph")
      .attr("transform", `translate(${srcMargin.left},${srcMargin.top})`);

    // add x and y axis
    this.yScale = d3.scaleLinear([time, 0], [height, 0]);
    this.yAxis = d3.axisLeft(this.yScale).ticks(height / 40);
    this.svg
      .append("g")
      .attr("class", "y-axis")
      .attr("transform", `translate(${width / 2},0)`)
      .call(this.yAxis);

    let xLimit = Math.max(Math.abs(d3.max(data)), Math.abs(d3.min(data))) * 1.1; // add 10% to the limits
    this.xScale = d3.scaleLinear([xLimit * -1, xLimit], [0, width]);
    this.xAxis = d3.axisTop(this.xScale);
    this.svg
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0,0)`)
      .call(this.xAxis);

    this.line = d3
      .line()
      .x((d) => this.xScale(d))
      .y((d, i) => {
        // 'i' is the index of the current data point
        // Calculate a 'conceptual' y-value between 0 and yLimit
        // based on index.
        const yValue = (i / (data.length - 1)) * time;
        
        return this.yScale(yValue); // Convert this conceptual yValue to an SVG pixel coordinate
      });

    this.path = this.svg
      .append("path")
      .datum(data) // Bind the entire data array (flat array of x-values)
      .attr("class", "line")
      .attr("d", this.line)
      .attr("stroke", "black")
      .attr("stroke-width", 2)
      .attr("fill", "none");

    // Add title for time-domain plot
    this.svg.append('text')
      .attr('class', 'graph-title')
      .attr('text-anchor', 'middle')
      .attr('x', width / 2)
      .attr('y', -25)
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('Time Domain');

    this.rescale(this.timeLimit);
    this.applySynchronizedTimeRange();
    
    // Render spectrum plot below the time-domain plot
    this.makeSpectrumPlot(src_data);
  }

  makeSpectrumPlot(src_data: any) {
    // Check if we have spectrum data
    if (!this.frequencies || !this.amplitudes || this.frequencies.length === 0 || this.amplitudes.length === 0) {
      console.log('No spectrum data available');
      return;
    }

    const fullContainerWidth = this.container.nativeElement.offsetWidth;
    const ratios = this.getWidthRatios();
    const containerWidth = fullContainerWidth * ratios.spectrum;
    const spectrumMargin = { top: 40, right: 20, bottom: 20, left: 60 }; 
    
    const width = containerWidth - spectrumMargin.left - spectrumMargin.right;
    const height = this.height;

    
    const spectrumData = this.frequencies.map((freq, index) => ({
      frequency: freq,
      amplitude: this.amplitudes[index]
    }));

    
    this.spectrumSvg = d3
      .select(this.graph.nativeElement.parentElement)
      .append('svg')
      .attr('class', 'spectrum-plot')
      .attr('width', width + spectrumMargin.left + spectrumMargin.right)
      .attr('height', height + spectrumMargin.top + spectrumMargin.bottom)
      .append('g')
      .attr('transform', `translate(${spectrumMargin.left},${spectrumMargin.top})`);

    // Set up spectrum scales
    this.spectrumXScale = d3.scaleLinear()
      .domain(d3.extent(this.frequencies))
      .range([0, width]);

    // Handle both positive and negative amplitude values
    const minAmplitude = d3.min(this.amplitudes);
    const maxAmplitude = d3.max(this.amplitudes);
    const amplitudeRange = Math.max(Math.abs(minAmplitude), Math.abs(maxAmplitude)) * 1.1;
    
    this.spectrumYScale = d3.scaleLinear()
      .domain([-amplitudeRange, amplitudeRange])
      .range([height, 0]);

    // Add spectrum axes
    this.spectrumXAxis = d3.axisBottom(this.spectrumXScale);
    this.spectrumYAxis = d3.axisLeft(this.spectrumYScale);

    const yZeroPosition = this.spectrumYScale(0);
    this.spectrumSvg.append('g')
      .attr('class', 'spectrum-x-axis')
      .attr('transform', `translate(0,${yZeroPosition})`)
      .call(this.spectrumXAxis);

    this.spectrumSvg.append('g')
      .attr('class', 'spectrum-y-axis')
      .call(this.spectrumYAxis);

    this.spectrumSvg.append('text')
  .attr('class', 'spectrum-y-label')
  .attr('text-anchor', 'middle')
  .attr('transform', 'rotate(-90)')
  .attr('x', -height / 2)
  .attr('y', -40)
  .style('font-size', '12px')
  .style('font-weight', 'normal')
  .text('Amplitude (dB)');

    // Add title and axis labels
    this.spectrumSvg.append('text')
      .attr('class', 'spectrum-title')
      .attr('text-anchor', 'middle')
      .attr('x', width / 2)
      .attr('y', -25)
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('Frequency Domain');

    // Create line generator
    this.spectrumLine = d3.line()
      .x((d: any) => this.spectrumXScale(d.frequency))
      .y((d: any) => this.spectrumYScale(d.amplitude))
      .curve(d3.curveMonotoneX);

    // Add spectrum line
    this.spectrumPath = this.spectrumSvg
      .append('path')
      .datum(spectrumData)
      .attr('class', 'spectrum-line')
      .attr('d', this.spectrumLine)
      .attr('stroke', '#2196F3')
      .attr('stroke-width', 2)
      .attr('fill', 'none');

    // Apply initial frequency limit
    this.rescaleSpectrum(this.frequencyLimit);
  }

  onTimeLimitChanged() {
    if (this.timeLimit == 0) this.timeLimit = this.totalTime;
    this.rescale(this.timeLimit);
  }

  onFrequencyLimitChanged() {
    if (this.frequencyLimit == 0) this.frequencyLimit = this.maxFrequency;
    this.rescaleSpectrum(this.frequencyLimit);
  }

  rescale(valueMax: number, valueMin: number = 0) {
    if (!valueMax) valueMax = this.totalTime;
  
    this.yScale.domain([valueMax, valueMin]);
    this.svg.select(".y-axis").call(this.yAxis);
    
    // Calculate data indices for both min and max time bounds
    const minTimeRatio = valueMin / this.totalTime;
    const maxTimeRatio = valueMax / this.totalTime;
    
    const minDataIndex = Math.floor(this.data.length * minTimeRatio);
    const maxDataIndex = Math.floor(this.data.length * maxTimeRatio);
    
    // Filter data points to only include those within the time bounds
    const filteredData = this.data.slice(minDataIndex, maxDataIndex);
    
    this.line.y((d, i) => {
      // Map the filtered data index to the time range
      const yValue = valueMin + (i / (filteredData.length - 1)) * (valueMax - valueMin);
      return this.yScale(yValue);
    });
    
    
    this.path
      .datum(filteredData)
      .attr("d", this.line);
  }

  rescaleSpectrum(freqMax: number, freqMin: number = 0) {
    if (!this.spectrumSvg || !this.frequencies || this.frequencies.length === 0) return;
    
    if (!freqMax) freqMax = this.maxFrequency;

    // Update x scale domain for frequency
    this.spectrumXScale.domain([freqMin, freqMax]);
    this.spectrumSvg.select(".spectrum-x-axis").call(this.spectrumXAxis);

    // Filter data points to only show within the frequency range
    const filteredData = this.frequencies
      .map((freq, index) => ({
        frequency: freq,
        amplitude: this.amplitudes[index]
      }))
      .filter(d => d.frequency >= freqMin && d.frequency <= freqMax);

    // Update the line path with filtered data
    this.spectrumPath
      .datum(filteredData)
      .attr("d", this.spectrumLine);

    // Update x-axis position based on new scale
    const yZeroPosition = this.spectrumYScale(0);
    this.spectrumSvg.select('.spectrum-x-axis')
      .attr('transform', `translate(0,${yZeroPosition})`);
  }

  updateHeight() {
    const srcMargin = { top: 40, right: 20, bottom: 20, left: 20 }; 
    // Update time-domain plot height
    d3.select(this.graph.nativeElement).attr(
      "height",
      this.height + srcMargin.top + srcMargin.bottom
    );
    if (this.svg) {
      this.svg.select("graph").attr("height", this.height);

      this.yScale.range([this.height, 0]);
      this.yAxis = d3.axisLeft(this.yScale).ticks(this.height / 40);
      this.svg.select(".y-axis").call(this.yAxis);

      this.xAxis = d3.axisTop(this.xScale);
      this.svg
        .select(".x-axis")
        .attr("transform", `translate(0,0)`)
        .call(this.xAxis);

      this.path.attr("d", this.line);
    }

    // Update spectrum plot height
    if (this.spectrumSvg && this.frequencies.length > 0 && this.amplitudes.length > 0) {
      const spectrumMargin = { top: 40, right: 20, bottom: 20, left: 60 };
      const height = this.height;

      // Update spectrum SVG height
      d3.select(this.graph.nativeElement.parentElement)
        .select('.spectrum-plot')
        .attr('height', height + spectrumMargin.top + spectrumMargin.bottom);
      
      this.spectrumYScale.range([height, 0]);
      this.spectrumYAxis = d3.axisLeft(this.spectrumYScale);

      // Update spectrum axes
      const yZeroPosition = this.spectrumYScale(0);
      this.spectrumSvg.select('.spectrum-x-axis')
        .attr('transform', `translate(0,${yZeroPosition})`)
        .call(this.spectrumXAxis);

      this.spectrumSvg.select('.spectrum-y-axis')
        .call(this.spectrumYAxis);

      // Update y-axis label position
      this.spectrumSvg.select('.spectrum-y-label')
        .attr('x', -height / 2);

      // Update spectrum line
      this.spectrumPath.attr('d', this.spectrumLine);
    }
  }
}
